import * as THREE from 'three'
import TWEEN from 'three/examples/jsm/libs/tween.module.js'
import { addVisualizerSceneModels } from 'Common/components/elements/visualizer/sceneModels'
import { OrbitControls } from 'Common/components/elements/visualizer/controls'
import { PerspectiveCamera } from 'Common/components/elements/visualizer/camera'
import { InteractionManager } from 'three.interactive'
import type { MarkerMeshType, VisualizerSceneOptions } from '../../../types/elements/visualizer/visualizerTypes'
import type { WaypointType } from 'Modules/types/pages/imageReview/waypoint'
import { MarkerType } from '../../../types/elements/visualizer/marker'
import { HeatmapMarkers } from './heatmapMarkers'
import { WaypointMarkers } from './waypointMarkers'
import { SpotLight } from './spotlight'

const defaultCallback = () => {
  return false
}

interface CameraOptions {
  fov
  near
  far
}

class VisualizerScene {
  constructor(
    stlUrl,
    options: VisualizerSceneOptions = {
      onLoadedCallback: defaultCallback,
      mouseInCallback: defaultCallback,
      mouseOutCallback: defaultCallback,
      clickedCallback: defaultCallback,
      sceneOptions: {},
    }
  ) {
    this.stlUrl = stlUrl
    this.sceneLoadedCallback = options?.onLoadedCallback
    this.markerMouseInCallback = options?.mouseInCallback
    this.markerMouseOutCallback = options?.mouseOutCallback
    this.markerClickedCallback = options?.clickedCallback
    this.sceneOptions = options?.sceneOptions
  }

  isOpen = true
  isAxesHelperEnabled = false
  scene: THREE.Scene
  sceneOptions
  sceneLoadedCallback
  camera: PerspectiveCamera
  controls: OrbitControls
  renderer
  containerParent
  container
  clock = new THREE.Clock()
  delta = 0
  interval = 1 / 30 // 30 FPS
  slowInterval = 1 / 30 // set to 30fps until model is loaded, then gets set to 0.1fps in addModel() to avoid lost context crashes
  rootGroup // Contains all other groups, sub-scenes, and 3D objects
  groups = {}
  spotLight
  spotLightHelper
  debug_spotlight = false
  markers
  markersData: Array<MarkerType>
  markerMouseInCallback
  markerMouseOutCallback
  markerClickedCallback
  stlUrl
  interactionManager
  selectedWaypoint: WaypointType // used during initializeWaypoints
  selectedMarker: MarkerType

  init(parentElm: HTMLElement, containerElm: HTMLElement, options?: { cameraOptions?: CameraOptions }) {
    this.intitializeContainer(parentElm, containerElm)
    this.setupRenderer()
    this.setupCamera(options?.cameraOptions)
    this.setupGroups()
    this.setupCamController()
    this.setupScene()
    this.controls.update()

    window.addEventListener('resize', this.handleResize)
  }

  handleResize = () => {
    this.camera.aspect = this.containerParent.clientWidth / this.containerParent.clientHeight
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(this.containerParent.clientWidth, this.containerParent.clientHeight)
  }

  destroy() {
    cancelAnimationFrame(this.animationFrameId)
    TWEEN.removeAll()
    window.removeEventListener('resize', this.handleResize)

    if (this.controls) {
      // OrbitControls adds many listeners to the DOM
      this.controls.dispose()
    }
    if (this.interactionManager) {
      this.interactionManager = null
    }

    if (this.scene) {
      this.scene.traverse((object) => {
        // Check if the object is a mesh with resources to dispose
        if (!object.isMesh) return

        // Dispose of geometries to free up GPU buffer memory
        if (object.geometry) {
          object.geometry.dispose()
        }

        // Dispose of materials and any associated textures
        if (object.material) {
          if (Array.isArray(object.material)) {
            // Handle multi-material objects
            object.material.forEach((material) => {
              if (material.map) material.map.dispose()
              material.dispose()
            })
          } else {
            // Handle single material objects
            if (object.material.map) object.material.map.dispose()
            object.material.dispose()
          }
        }
      })

      // Remove all children from the scene to ensure the object graph is broken
      while (this.scene.children.length > 0) {
        this.scene.remove(this.scene.children[0])
      }
    }

    if (this.renderer) {
      // This clears the WebGL context and frees up significant GPU resources.
      this.renderer.dispose()

      // Remove the <canvas> element from the page
      if (this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement)
      }
    }

    this.renderer = null
    this.scene = null
    this.camera = null
    this.controls = null
    this.rootGroup = null
    this.markers = null
    this.container = null
    this.containerParent = null
  }

  animate() {
    if (!this.renderer) {
      return
    }

    this.animationFrameId = requestAnimationFrame(this.animate.bind(this))

    this.delta += this.clock.getDelta()
    this.interactionManager.update()
    TWEEN.update()

    if (this.isOpen) {
      if (this.delta > this.interval) {
        this.renderer.render(this.scene, this.camera)
        if (this.spotLightHelper != null) this.spotLightHelper.update()
        this.delta = this.delta % this.interval
      }
    } else {
      if (this.delta > this.slowInterval) {
        this.renderer.render(this.scene, this.camera)
        this.delta = this.delta % this.slowInterval
      }
    }
  }

  intitializeContainer(parentElm: HTMLElement, containerElm: HTMLElement) {
    if (parentElm && containerElm) {
      this.containerParent = parentElm
      this.container = containerElm
    } else {
      throw new Error(
        `VisualizerScene Error - Bad implementation. Must provide both parent and container elements to initialize`
      )
    }
  }

  initializeGroup(groupName, data, callback, selectedMarker) {
    this.selectedMarker = selectedMarker
    let group
    try {
      if (!this.groups[groupName]) {
        group = this.groups[groupName] = new THREE.Group()
        group.name = groupName
        group.rotation.set(-Math.PI / 2, 0, Math.PI / 2)
        this.scene.add(group)
      } else {
        group = this.groups[groupName]
      }
    } catch (err) {
      throw new Error(`Could not create/add group. Check group name ${groupName} meets Object[key] requirements`)
    }

    // TODO: figure out how to make below more generic so we dont specify heatmap
    if (groupName == 'heatmap') {
      this.markers = new HeatmapMarkers(
        data,
        (type, model) => callback(this, type, model),
        this.interactionManager,
        group,
        this.selectedMarker,
        true,
        group
      )
    }

    if (groupName == 'waypoints') {
      this.markers = new WaypointMarkers(
        data,
        (type, model) => callback(this, type, model),
        this.interactionManager,
        this.rootGroup,
        this.selectedMarker,
        false,
        group
      )
    }
  }

  initializeSpotLight() {
    this.spotLight = new SpotLight(this.rootGroup)

    this.moveToMarker(this.markers.selectedMarkerModel.mesh, { updateSpotLight: true })
  }

  setupCamera(options: CameraOptions = { fov: 30, near: 0.01, far: 250 }) {
    this.camera = new PerspectiveCamera(
      options.fov,
      this.containerParent.clientWidth / this.containerParent.clientHeight,
      options.near,
      options.far
    )

    this.camera.name = 'maincam'
    this.scene.add(this.camera)
  }

  setupCamController() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.target.set(0, 0, 0)
    this.controls.minDistance = 1
    this.controls.maxDistance = 50
    this.controls.minPolarAngle = 0
    this.controls.maxPolarAngle = Math.PI / 2
    this.controls.update()
  }

  setupGroups() {
    this.rootGroup = new THREE.Group()
    this.rootGroup.name = 'root'
    this.rootGroup.rotation.set(-Math.PI / 2, 0, Math.PI / 2)
    this.scene.add(this.rootGroup)
  }

  setupRenderer() {
    this.scene = new THREE.Scene()
    this.scene.fog = new THREE.FogExp2(0x000000, 0.00875)
    this.renderer = new THREE.WebGLRenderer({ antialias: true })
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.shadowMap.enabled = true
    this.renderer.autoClear = false
    this.renderer.physicallyCorrectLights = true
    this.renderer.outputEncoding = THREE.sRGBEncoding
    this.renderer.shadowMap.enabled = true
    this.renderer.setSize(this.containerParent.clientWidth, this.containerParent.clientHeight)
    this.container.appendChild(this.renderer.domElement)
  }

  setupScene() {
    addVisualizerSceneModels(this.rootGroup, this.stlUrl, {
      loadedCallback: () => {
        this.animate()
        this.sceneLoadedCallback()
      },
      ...this.sceneOptions,
    })
    //TODO Research proper arguments to pass into interaction manager
    this.interactionManager = new InteractionManager(this.renderer, this.camera, this.renderer.domElement)

    if (this.isAxesHelperEnabled) {
      const axesHelper = new THREE.AxesHelper(5)
      this.scene.add(axesHelper)
    }
  }

  moveToMarker(marker: MarkerMeshType, options?) {
    this.controls.setTargetByObjectPosition(marker)
    this.camera.setCameraPositionByObjectPosition(marker)
    if (options.updateSpotLight) this.spotLight.setSpotLightPositionAndRotationByObject(marker)
  }

  //TODO Move to own file
  onWindowResize(): void {
    this.camera.aspect = this.containerParent.clientWidth / this.containerParent.clientHeight
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(this.containerParent.clientWidth, this.containerParent.clientHeight)
  }

  /**
   * Toggle the open state of the scene; which impacts the scene frame rate
   */
  toggleOpen(): void {
    if (this.isOpen) {
      this.isOpen = false
    } else {
      this.isOpen = true
    }
  }
}

export { VisualizerScene }
